// SPDX-License-Identifier: AGPL-3.0-or-later

import {ExternalLink} from '@app/features/app/components/shared/ExternalLink';
import {PRODUCT_NAME, SUPPORT_EMAIL, SUPPORT_EMAIL_MAILTO} from '@app/features/app/config/I18nDisplayConstants';
import RuntimeConfig from '@app/features/app/state/RuntimeConfig';
import styles from '@app/features/auth/components/AuthLegalLinks.module.css';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';

const TERMS_DESCRIPTOR = msg({
	message: 'Terms of Service',
	comment: 'Footer link under the sign-in and registration forms.',
});
const PRIVACY_DESCRIPTOR = msg({message: 'Privacy', comment: 'Footer link under the sign-in and registration forms.'});
const REFUNDS_DESCRIPTOR = msg({
	message: 'Refunds',
	comment: 'Footer link under the sign-in and registration forms, to the refund policy.',
});
const CONTACT_DESCRIPTOR = msg({
	message: 'Contact: {email}',
	comment: 'Footer link under the sign-in and registration forms; {email} is the support address.',
});
const ABOUT_DESCRIPTOR = msg({
	message: 'About {productName}',
	comment: 'Footer link under the sign-in and registration forms, to the product page.',
});

// The legal pages beside the product page (micup.gg/yasal), in Turkish and in English.
const LEGAL_PAGES = {
	tr: {terms: 'kullanim-sartlari', privacy: 'gizlilik', refunds: 'iade-politikasi'},
	en: {terms: 'en/terms', privacy: 'en/privacy', refunds: 'en/refund'},
} as const;

// A signed-out visitor sees the auth pages first: the terms and a contact are one click away there. Before
// the runtime config is there (the auth runtime target boundary still resolving), nothing shows.
export const AuthLegalLinks = () => {
	const {i18n} = useLingui();
	const marketingEndpoint = RuntimeConfig.getSnapshotOrNull()?.marketingEndpoint;
	if (!marketingEndpoint) return null;
	const pages = i18n.locale.startsWith('tr') ? LEGAL_PAGES.tr : LEGAL_PAGES.en;
	const legal = (page: string) => new URL(`/yasal/${page}`, marketingEndpoint).href;
	const links = [
		[i18n._(TERMS_DESCRIPTOR), legal(pages.terms)],
		[i18n._(PRIVACY_DESCRIPTOR), legal(pages.privacy)],
		[i18n._(REFUNDS_DESCRIPTOR), legal(pages.refunds)],
		[i18n._(CONTACT_DESCRIPTOR, {email: SUPPORT_EMAIL}), SUPPORT_EMAIL_MAILTO],
		[i18n._(ABOUT_DESCRIPTOR, {productName: PRODUCT_NAME}), marketingEndpoint],
	];
	return (
		<nav className={styles.links} data-flx="auth.auth-legal-links.links">
			{links.map(([label, href]) => (
				<ExternalLink key={href} href={href} className={styles.link} data-flx="auth.auth-legal-links.link">
					{label}
				</ExternalLink>
			))}
		</nav>
	);
};
